import { useState } from 'react';
import { geocodeAddress } from '../../services/locationService';

// Shared GPS-coordinate picker used by admin shop forms.
//   formData   — must contain { lat, lng, locationVerified, locationAddress,
//                                address, city } from the parent's form state.
//   onPatch(p) — called with a partial { lat, lng, locationVerified,
//                locationAddress } whenever the user picks or clears a spot.
export default function LocationSearchPanel({ formData, onPatch }) {
  const [search, setSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [showManual, setShowManual] = useState(false);

  async function searchAddress(query) {
    if (!query?.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const data = await geocodeAddress(query);
      if (data.length > 0) {
        setResults(data);
        setError(null);
      } else {
        setResults(null);
        setError('Adres niet gevonden. Probeer specifieker.');
      }
    } catch {
      setError('Zoeken mislukt. Controleer je verbinding.');
    }
    setSearching(false);
  }

  function confirmLocation(result) {
    onPatch({
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      locationVerified: true,
      locationAddress: result.display_name,
    });
    setResults(null);
    setSearch('');
  }

  function clearLocation() {
    onPatch({ lat: null, lng: null, locationVerified: false, locationAddress: '' });
  }

  const hasCoords =
    formData?.lat != null && formData?.lng != null &&
    !Number.isNaN(parseFloat(formData.lat)) && !Number.isNaN(parseFloat(formData.lng));

  return (
    <div className="admin-card">
      <div className="admin-card__title">📍 Exacte winkellocatie</div>

      {hasCoords ? (
        <div className="admin__location-verified">
          <span>✅</span>
          <div>
            <div className="admin__location-verified-title">Locatie ingesteld</div>
            <div className="admin__location-verified-coords">
              {parseFloat(formData.lat).toFixed(6)}, {parseFloat(formData.lng).toFixed(6)}
            </div>
            {formData.locationAddress && (
              <div className="admin__location-verified-coords">
                {formData.locationAddress}
              </div>
            )}
          </div>
          <button type="button" className="admin__location-clear" onClick={clearLocation}>✕</button>
        </div>
      ) : (
        <div className="admin__location-empty">
          ⚠️ Geen locatie — klanten zien geen exacte afstand en geen pin op kaart
        </div>
      )}

      <div className="admin__location-search">
        <div className="admin__location-search-row">
          <input
            type="text"
            className="admin__location-input"
            placeholder="Zoek adres… bijv. Beethovenstraat 1, Amsterdam"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddress(search))}
          />
          <button
            type="button"
            className="admin__location-search-btn"
            onClick={() => searchAddress(search)}
            disabled={searching || !search.trim()}
          >
            {searching ? '…' : '🔍'}
          </button>
        </div>

        {formData?.address && formData?.city && (
          <button
            type="button"
            className="admin__location-autofill"
            onClick={() => {
              const q = `${formData.address}, ${formData.city}`;
              setSearch(q);
              searchAddress(q);
            }}
          >
            📋 Gebruik winkeladres: {formData.address}, {formData.city}
          </button>
        )}

        {results && results.length > 0 && (
          <div className="admin__location-results">
            {results.map((r, i) => (
              <button
                type="button"
                key={i}
                className="admin__location-result"
                onClick={() => confirmLocation(r)}
              >
                <span className="admin__location-result-name">📍 {r.display_name}</span>
                <span className="admin__location-result-coords">
                  {parseFloat(r.lat).toFixed(4)}, {parseFloat(r.lon).toFixed(4)}
                </span>
              </button>
            ))}
          </div>
        )}

        {error && <div className="admin__location-error">{error}</div>}
      </div>

      <button
        type="button"
        className="admin__location-manual-toggle"
        onClick={() => setShowManual(!showManual)}
      >
        {showManual ? '▲' : '▼'} Handmatig coördinaten invoeren
      </button>

      {showManual && (
        <div className="admin__location-manual">
          <div className="admin__location-manual-row">
            <div className="admin-field">
              <span className="admin-field__label">Latitude</span>
              <input
                className="admin-input"
                type="number"
                step="0.000001"
                placeholder="52.336700"
                value={formData?.lat ?? ''}
                onChange={(e) =>
                  onPatch({
                    lat: e.target.value === '' ? null : parseFloat(e.target.value),
                    locationVerified: e.target.value !== '',
                  })
                }
              />
            </div>
            <div className="admin-field">
              <span className="admin-field__label">Longitude</span>
              <input
                className="admin-input"
                type="number"
                step="0.000001"
                placeholder="4.877400"
                value={formData?.lng ?? ''}
                onChange={(e) =>
                  onPatch({
                    lng: e.target.value === '' ? null : parseFloat(e.target.value),
                    locationVerified: e.target.value !== '',
                  })
                }
              />
            </div>
          </div>
          <div style={{ fontSize: 11, color: '#6B7280', marginTop: 4 }}>
            💡 Tip: Rechtermuisknop op Google Maps → "Wat is hier?" → kopieer de coördinaten
          </div>
        </div>
      )}
    </div>
  );
}
